import { useCallback } from 'react'

import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import find from 'lodash-es/find'
import map from 'lodash-es/map'

import type { TypePreferences } from '@b-stock/notification-api-client'
import { ChannelSettingsChannelEnum as Channel } from '@b-stock/notification-api-client'

import api from '@api/notification'
import useUserInfo from '@helpers/useUserInfo'

type NotificationTypeChannelState = {
  active: boolean
  editable: boolean
  visible: boolean
}

type NotificationTypePreference = {
  title: string
  description: string
  id: string
  emailState: NotificationTypeChannelState
  smsState: NotificationTypeChannelState
  parentGroup?: string
}

const getTypeChannelState = (
  typePref: TypePreferences,
  channel: Channel
): NotificationTypeChannelState => {
  const settings = typePref.channelSettings.find(
    (chanSettings) => chanSettings.channel === channel
  )

  return {
    active: !!typePref.channels.find(({ type }) => type === channel)?.active,
    editable: settings?.allowsOptOut ?? false,
    visible: settings?.supported ?? false,
  }
}

const getKey = (personId: string) => ['fetchNotificationPreferences', personId]

const useNotificationPreferences = (
  opts?: Omit<
    UseQueryOptions<
      unknown,
      unknown,
      NotificationTypePreference[] | undefined,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { personId } = useUserInfo()
  const queryClient = useQueryClient()

  if (!personId) {
    throw new Error('Unable to extract a personId from the store')
  }

  const query = useQuery({
    queryKey: getKey(personId),

    queryFn: async () => {
      const data = await api.fetchNotificationPreferences(personId)
      const types: NotificationTypePreference[] = []

      data.forEach((notification) => {
        notification.group.types?.forEach((typePref) => {
          types.push({
            title: typePref.label,
            description: typePref.description || 'MISSING_DESCRIPTION',
            id: typePref.id,
            emailState: getTypeChannelState(typePref, Channel.Email),
            smsState: getTypeChannelState(typePref, Channel.Sms),
            parentGroup: notification.parentGroup?.label,
          })
        })
      })

      types.sort((a, b) => {
        if (a.title === b.title) {
          return 0
        }

        return a.title > b.title ? 1 : -1
      })
      return types
    },
    ...opts,
  })

  const updatePreference = useCallback(
    async (
      typeId: string,
      channel: Channel,
      active: boolean
    ): Promise<NotificationTypePreference | undefined> => {
      const key = getKey(personId)

      const updateCache = (active: boolean) => {
        queryClient.setQueryData(
          key,
          (oldData?: NotificationTypePreference[]) =>
            map(oldData, (typePref) => {
              if (typePref.id === typeId) {
                let pref = null

                if (channel === Channel.Email) {
                  pref = { emailState: { ...typePref.emailState, active } }
                } else if (channel === Channel.Sms) {
                  pref = { smsState: { ...typePref.smsState, active } }
                }

                return {
                  ...typePref,
                  ...pref,
                }
              }

              return typePref
            })
        )
      }

      try {
        updateCache(active)
        await api.updateNotificationPreference(
          personId,
          typeId,
          channel,
          active
        )
      } catch {
        updateCache(!active)
      }
      return find<NotificationTypePreference>(queryClient.getQueryData(key), {
        id: typeId,
      })
    },
    [queryClient, personId]
  )

  return {
    updatePreference,
    ...query,
  }
}

export { useNotificationPreferences }

export type { NotificationTypeChannelState, NotificationTypePreference }
